import React, { useEffect, useState } from "react";

import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import addCommas from "@src/utils/CommaHelperFunctions/addCommas";
import removeCommas from "@src/utils/CommaHelperFunctions/removeCommas";

import styles from "./FormCellTextField.module.css";

interface TextFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  containerClassName?: string;
  inputClassName?: string;
  inputType?: string;
}

// This simple text field component needs to be used in the editable table.
// Our FormTextField component which uses MUI was causing serious performance issues in the editable table.
const TextField: React.FC<TextFieldProps> = ({
  leftIcon,
  rightIcon,
  containerClassName = "",
  inputClassName = "",
  value,
  onChange,
  name,
  inputType,
  ...rest
}) => {
  const [displayValue, setDisplayValue] = useState<string>(value as string);
  const isNumberField = inputType === "number";

  useEffect(() => {
    if (
      isNumberField &&
      typeof value === "string" &&
      value !== "" &&
      !isNaN(Number(removeCommas(value)))
    ) {
      const numValue = Number(removeCommas(value));
      if (numValue === 0) {
        if (value.includes(".")) {
          setDisplayValue(value);
        } else {
          setDisplayValue("0");
        }
      } else {
        setDisplayValue(addCommas(value));
      }
    } else {
      setDisplayValue(value as string);
    }
  }, [value, isNumberField]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (
      isNumberField &&
      inputValue !== "" &&
      !isNaN(Number(removeCommas(inputValue)))
    ) {
      const rawValue = removeCommas(inputValue);
      const numValue = Number(rawValue);

      if (numValue === 0) {
        if (inputValue.includes(".")) {
          setDisplayValue(inputValue);
        } else {
          setDisplayValue("0");
        }
      } else {
        setDisplayValue(addCommas(inputValue));
      }

      if (onChange) {
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: rawValue,
          },
        };
        onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
      }
    } else {
      setDisplayValue(inputValue);
      if (onChange) {
        onChange(e);
      }
    }
  };

  return (
    <div className={toClassName(styles.container, containerClassName)}>
      {leftIcon && (
        <div className={toClassName(styles.icon, styles.leftIcon)}>
          {leftIcon}
        </div>
      )}
      <input
        type="text"
        className={toClassName(styles.input, inputClassName)}
        value={displayValue}
        onChange={handleChange}
        name={name}
        {...rest}
      />
      {rightIcon && (
        <div className={toClassName(styles.icon, styles.rightIcon)}>
          {rightIcon}
        </div>
      )}
    </div>
  );
};

export default TextField;
